/**
 * TOSS Web Application - Error Messages Constants
 *
 * Centralized Russian error message constants for consistent error display.
 * Maps backend error codes, HTTP status codes, and system errors to user-friendly Russian messages.
 */

// ============================================================================
// Error Codes (Backend)
// ============================================================================

/**
 * Backend error codes mapped to user-friendly Russian messages
 * These codes match the error codes returned by the API
 */
export const ERROR_MESSAGES: Record<string, string> = {
  // ── General ──────────────────────────────────────────────────────
  UNAUTHORIZED: 'Требуется авторизация. Пожалуйста, войдите в систему.',
  BAD_REQUEST: 'Некорректные данные. Проверьте введённую информацию.',
  INVALID_OPERATION: 'Операция не может быть выполнена.',
  NOT_FOUND: 'Запрашиваемый ресурс не найден.',
  INTERNAL_ERROR: 'Произошла ошибка сервера. Попробуйте позже.',
  API_ERROR: 'Произошла ошибка. Попробуйте позже.',
  VALIDATION_ERROR: 'Проверьте правильность заполнения полей.',
  FORBIDDEN: 'У вас нет прав для выполнения этого действия.',
  ACCESS_DENIED: 'Недостаточно прав. Обратитесь к администратору.',
  // Generic FE-only fallbacks (no exact backend code, kept intentionally).
  CONFLICT: 'Конфликт данных. Обновите страницу и попробуйте снова.',
  TOKEN_EXPIRED: 'Сессия истекла. Пожалуйста, войдите в систему заново.',
  RESOURCE_LOCKED: 'Ресурс заблокирован. Попробуйте позже.',
  RATE_LIMIT_EXCEEDED: 'Слишком много запросов. Подождите немного.',

  // ── Entity ───────────────────────────────────────────────────────
  // ENTITY_IN_USE — фолбэк: если бэкенд прислал свою (более точную) причину,
  // getErrorMessage() показывает её вместо этого текста.
  ENTITY_IN_USE: 'Невозможно удалить: элемент используется в других записях.',
  SERVICE_INACTIVE: 'Услуга деактивирована — её нельзя добавить в заявку.',
  SERVICE_NOT_FOR_CUSTOMER: 'Эта услуга доступна только отдельным клиентам — её нельзя добавить в заявку этого клиента.',
  DUPLICATE_ENTITY: 'Такая запись уже существует. Укажите другие значения.',

  // ── Auth ─────────────────────────────────────────────────────────
  INVALID_CREDENTIALS: 'Неверный логин или пароль.',
  USER_NOT_FOUND: 'Пользователь не найден.',
  USER_DELETED: 'Учётная запись удалена. Обратитесь к администратору.',
  USER_BLOCKED: 'Ваш аккаунт заблокирован. Обратитесь к администратору.',
  USER_REJECTED: 'Ваша регистрация была отклонена. Обратитесь к администратору.',
  USER_LOCKED_OUT: 'Аккаунт временно заблокирован из-за неудачных попыток входа. Попробуйте позже.',
  WEAK_PASSWORD: 'Пароль слишком простой. Используйте минимум 8 символов, буквы и цифры.',
  EMAIL_TAKEN: 'Пользователь с таким email уже существует. Укажите другой адрес.',
  REGISTRATION_FAILED: 'Не удалось зарегистрироваться. Проверьте данные или обратитесь к администратору.',
  USER_CREATION_FAILED: 'Не удалось создать пользователя. Проверьте данные или обратитесь к администратору.',
  USER_DELETION_FAILED: 'Не удалось удалить пользователя. Попробуйте позже.',
  PASSWORD_RESET_FAILED: 'Не удалось сбросить пароль. Запросите сброс заново.',
  RESET_FAILED: 'Не удалось выполнить сброс пароля. Запросите ссылку заново.',
  CHANGE_FAILED: 'Не удалось сменить пароль. Проверьте текущий пароль и требования к новому.',
  INVALID_RESET_REQUEST: 'Некорректный запрос на сброс пароля. Запросите новую ссылку.',
  INVALID_RESET_TOKEN: 'Ссылка для сброса пароля недействительна. Запросите новую.',
  RESET_TOKEN_EXPIRED: 'Срок действия ссылки для сброса пароля истёк. Запросите новую.',
  CANNOT_ASSIGN_TO_ROLE: 'Нельзя назначить пользователя на эту роль.',
  INVALID_ROLE: 'Указана недопустимая роль. Выберите роль из списка.',
  PTO_REQUIRED_FOR_ROLE: 'Для этой роли необходимо выбрать ПТО.',
  RULES_NOT_ACCEPTED: 'Необходимо принять условия и правила, чтобы продолжить.',
  INVALID_MENTOR: 'Указан недопустимый наставник. Выберите наставника из списка.',

  // ── Directory: Customer ──────────────────────────────────────────
  INN_TAKEN: 'Контрагент с таким ИНН уже существует. Укажите другой ИНН.',

  // ── Directory: Customer Portal Users (client portal, internal LK) ─
  // EMAIL_TAKEN — уже есть в блоке «Auth» выше (используется тем же кодом на бэкенде).
  EMAIL_TAKEN_BY_DELETED_USER: 'Этот email уже использовался: учётная запись удалена, восстановление не поддерживается. Укажите другой адрес.',
  PORTAL_ACCOUNT_BLOCKED: 'Учётная запись заблокирована — сначала разблокируйте её, затем привязывайте юрлицо.',
  LAST_COMPANY: 'Это единственное юрлицо учётной записи. Чтобы закрыть доступ, заблокируйте или удалите учётную запись.',

  // ── Directory: Container ─────────────────────────────────────────
  // Конфликты при добавлении контейнеров/энергоустановок в заявку.
  // Это НЕ ошибки доступа (нехватка прав всегда даёт 403) — пользователь может
  // решить их сам, поэтому сообщения конкретные и подсказывают действие.
  CONTAINER_NUMBER_TAKEN: 'Контейнер с таким номером уже есть в справочнике. Добавьте существующий контейнер вместо создания нового.',
  CONTAINER_EXISTS: 'Контейнер с таким номером уже есть в справочнике. Добавьте существующий контейнер.',
  CONTAINER_ALREADY_EXISTS: 'Этот контейнер уже добавлен в ПТО.',
  CONTAINER_ID_REQUIRED: 'Не выбран контейнер. Выберите контейнер из списка.',
  CONTAINER_NOT_ASSIGNED: 'Этот контейнер не привязан к ПТО.',

  // ── Directory: Equipment ─────────────────────────────────────────
  EQUIPMENT_NUMBER_TAKEN: 'Энергетическая установка с таким номером уже есть в справочнике. Добавьте существующую установку.',
  EQUIPMENT_EXISTS: 'Энергетическая установка с таким номером уже есть в справочнике. Добавьте существующую установку.',
  EQUIPMENT_ALREADY_EXISTS: 'Эта энергетическая установка уже добавлена в ПТО.',
  EQUIPMENT_ID_REQUIRED: 'Не выбрана энергетическая установка. Выберите установку из списка.',
  EQUIPMENT_NOT_ASSIGNED: 'Эта энергетическая установка не привязана к ПТО.',

  // ── Directory: PTO ───────────────────────────────────────────────
  PTO_NAME_TAKEN: 'ПТО с таким названием уже существует. Укажите другое название.',
  PTO_ALREADY_ASSIGNED: 'ПТО уже назначено. Обновите страницу и попробуйте снова.',
  // Affiliated PTOs (2026-09-05): HEAD_OF_PTO_NOT_FOUND removed with the head-of-pto picker —
  // its producer is gone from the backend.
  PTO_AFFILIATION_SELF: 'ПТО не может быть главным для самого себя.',
  PTO_AFFILIATION_DEPTH: 'Аффилиация только одного уровня: главное ПТО не может быть подчинённым, а у подчинённого не бывает своих.',
  PTO_HAS_HEAD_OF_PTO: 'У этого ПТО есть начальники ПТО — сначала переведите их на главное ПТО.',
  HEAD_OF_PTO_REQUIRES_ROOT_PTO: 'Начальник ПТО назначается только на главное ПТО группы.',
  PTO_OUTSIDE_GROUP: 'ПТО не входит в вашу группу.',

  // ── Directory: Service ───────────────────────────────────────────
  SERVICE_NAME_TAKEN: 'Услуга с таким названием уже существует. Укажите другое название.',
  SERVICE_ALREADY_EXISTS: 'Такая услуга уже существует.',
  SERVICE_ALREADY_ADDED: 'Эта услуга уже добавлена в ПТО.',
  INVALID_SERVICE_TYPE: 'Недопустимый тип услуги для этой операции.',

  // ── Directory: Direction ─────────────────────────────────────────
  DIRECTION_NAME_TAKEN: 'Направление с таким названием уже существует. Укажите другое название.',

  // ── Directory: Material ──────────────────────────────────────────
  MATERIAL_NAME_TAKEN: 'Материал с таким названием уже существует. Укажите другое название.',
  MATERIAL_CODE_TAKEN: 'Материал с таким кодом уже существует. Укажите другой код.',

  // ── Directory: Instruction ───────────────────────────────────────
  INSTRUCTION_NAME_TAKEN: 'Инструкция с таким названием уже существует. Укажите другое название.',

  // ── Application ──────────────────────────────────────────────────
  APPLICATION_CANCELLED: 'Заявка отменена — редактирование недоступно.',
  APPLICATION_COMPLETED: 'Заявка завершена — можно изменить только комментарий.',
  ALREADY_CANCELLED: 'Заявка уже отменена.',
  TITLE_TAKEN: 'Запись с таким названием уже существует. Укажите другое название.',
  INVALID_STATUS_TRANSITION: 'Недопустимое изменение статуса. Обновите страницу и попробуйте снова.',
  INVALID_STATUS: 'Действие недоступно в текущем статусе. Обновите страницу и попробуйте снова.',
  UNCONFIRMED_EXPENSES: 'Есть неподтверждённые расходы. Подтвердите или отклоните их, прежде чем продолжить.',

  // ── Application PTO ──────────────────────────────────────────────
  PTO_ALREADY_CLAIMED: 'ПТО уже взято в работу другим механиком.',
  PTO_ALREADY_EXAMINED: 'Осмотр ПТО уже начат — действие недоступно.',
  PTO_NOT_VERIFIED_BY_FMO: 'ПТО должно быть рассмотрено сотрудником ФМО перед выставлением счёта.',
  // Единственный «обратитесь к другому человеку» случай — и он приходит как 403, не 409.
  ALREADY_VERIFIED: 'Заявка проверена ФМО. Для изменений обратитесь к ФМО за разблокировкой.',
  NOT_VERIFIED: 'ПТО должно быть верифицировано перед выставлением счёта.',
  NOT_A_MECHANIC: 'Брать ПТО в работу могут только механики и стажёры.',
  MECHANIC_NOT_APPROVED: 'Механик не подтверждён. Назначать можно только подтверждённых механиков.',
  WRONG_PTO: 'Это ПТО относится к другому подразделению. Действие недоступно.',
  NOT_YOUR_PTO: 'Это ПТО назначено другому механику.',
  INVALID_MECHANIC: 'Указан недопустимый механик. Выберите механика из списка.',
  MECHANIC_NOT_ASSIGNED: 'Механик не назначен на это ПТО.',
  ITEM_ALREADY_EXISTS: 'Этот предмет уже добавлен в ПТО.',
  ITEM_HAS_INSPECTION: 'Нельзя убрать осмотренный предмет из заявки. Сначала удалите его осмотр.',
  COEFFICIENT_REQUIRED: 'Укажите коэффициент для этой услуги.',
  INVALID_COUNTS: 'Количество исправных не может превышать общее количество. Проверьте значения.',
  INVALID_REORDER: 'Не удалось изменить порядок ПТО. Обновите страницу и попробуйте снова.',
  REASON_REQUIRED: 'Укажите причину.',
  REASON_TEXT_REQUIRED: 'Укажите текст причины.',

  // ── Inspection ───────────────────────────────────────────────────
  INSPECTION_EXISTS: 'Осмотр этого предмета уже существует.',
  INSPECTION_ALREADY_STARTED: 'Осмотр уже начат — действие недоступно.',
  INSPECTION_PTO_MISMATCH: 'Осмотр относится к другому ПТО. Обновите страницу и попробуйте снова.',

  // ── Tasks ────────────────────────────────────────────────────────
  CANNOT_CREATE_TASKS: 'У вашей роли нет прав на создание задач. Обратитесь к администратору.',
  TASK_NOT_MODIFIABLE: 'Эту задачу нельзя изменить в текущем статусе.',
  TASK_NOT_IN_WORK_STATUS: 'Добавлять отчёт можно только к задачам в статусе «В работе».',
  TASK_NOT_CANCELLABLE: 'Эту задачу нельзя отменить в текущем статусе.',
  TASK_EDIT_FORBIDDEN: 'У вас нет прав на редактирование этой задачи.',
  TASK_CANCEL_FORBIDDEN: 'У вас нет прав на отмену этой задачи.',
  TASK_DELETION_DISABLED: 'Удаление задач отключено.',
  REPORT_ALREADY_EXISTS: 'Отчёт по этой задаче уже добавлен.',
  REPORT_FORBIDDEN: 'У вас нет прав на добавление отчёта.',

  // ── Expenses ─────────────────────────────────────────────────────
  EXPENSE_NOT_MODIFIABLE: 'Этот расход нельзя изменить в текущем статусе.',
  EXPENSE_NOT_PENDING: 'Действие доступно только для расходов на рассмотрении.',

  // ── Files ────────────────────────────────────────────────────────
  FILE_UPLOAD_FAILED: 'Не удалось загрузить файл. Попробуйте ещё раз.',
  FILE_DOWNLOAD_FAILED: 'Не удалось скачать файл. Попробуйте позже.',
  INVALID_FILE_TYPE: 'Недопустимый тип файла. Выберите файл другого формата.',
  INVALID_DOCUMENT_TYPE: 'Недопустимый тип документа.',

  // ── Messenger ────────────────────────────────────────────────────
  INVALID_MEMBERS: 'Указаны недопустимые участники чата. Проверьте список.',

  // Запасные фразы: все три кода входят в PREFER_BACKEND_MESSAGE_CODES (api/client.ts), и обычно
  // виден текст сервера — он один знает, ЧЬИ учётные записи не подошли и почему.
  CUSTOMER_CHAT_MEMBER_INVALID: 'Кто-то из выбранных людей не подходит для этого чата. Проверьте списки обеих сторон.',
  CUSTOMER_CHAT_HAS_MESSAGES: 'В этом чате уже есть переписка — её нельзя стереть. Уберите из чата ненужных участников.',
  CUSTOMER_CHAT_NOT_SHARED: 'Удалить можно только дополнительный чат с заказчиком. Общий чат компании не удаляется.',
  INVALID_TARGET: 'Недопустимый адресат чата. Выберите другого получателя.',
  PRIVATE_CHAT: 'Это действие недоступно для личного чата.',
  CANNOT_DELETE_PRIVATE_CHAT: 'Личный чат удалить нельзя.',
  ALREADY_MEMBER: 'Этот пользователь уже состоит в чате.',
  INSUFFICIENT_ROLE: 'Недостаточно прав. Обратитесь к администратору.',
  EMPTY_MESSAGE: 'Сообщение не может быть пустым.',
  MESSAGE_DELETED: 'Сообщение удалено.',

  // ── Account Deletion ─────────────────────────────────────────────
  REQUEST_ALREADY_EXISTS: 'Запрос уже отправлен и ожидает обработки.',
  REQUEST_ALREADY_PROCESSED: 'Этот запрос уже обработан.',

  // ── Email Gateway (client portal Р6) ──────────────────────────────
  // Fallback only — PREFER_BACKEND_MESSAGE_CODES (api/client.ts) shows the backend's
  // own sentence instead whenever it names the letter's real state.
  EMAIL_GATEWAY_INVALID_STATE: 'Письмо уже обработано другим диспетчером. Обновите список.',
  EMAIL_GATEWAY_RAW_UNAVAILABLE: 'Письмо целиком не скачивалось (больше лимита) или срок хранения истёк.',

  // ── Inspection flow (redesign, Phase 2; Phase 5 — PM marks, 2026-09-03) ───────────
  // INVALID_COUNTS, REASON_REQUIRED already exist above (Application PTO block) with
  // generic-enough wording — not restated here. INSPECTION_INCOMPLETE has no entry by
  // design: it is never toasted, only rendered as a blockers[] checklist. PHOTO_REQUIRED
  // removed (Phase 5): a photo is never required anywhere in the flow any more — a missing
  // one is the PHOTO_MISSING warning, not a 400.
  // Fallback only: every screen that can raise this code opens its own confirmation dialog, which
  // names what will be deleted. If this text ever reaches a user, a dialog is missing.
  UNITS_ALREADY_FILLED: 'Осмотр уже начат в другом виде — подтвердите переключение.',
  REASON_SCOPE_MISMATCH: 'Эта причина не применяется к текущему блоку.',
  REASON_COMMENT_REQUIRED: 'Для выбранной причины нужно пояснение.',
  REASON_INACTIVE: 'Причина выведена из справочника — выберите действующую.',
  REPAIR_DESCRIPTION_REQUIRED: 'Опишите, что требует ремонта.',
  CONTAINER_NUMBER_REQUIRED: 'Укажите контейнер или его номер.',
  CONTAINER_ALREADY_LISTED: 'Этот контейнер уже есть в списке.',
  HANDOVER_ALREADY_RESOLVED: 'На эту передачу уже ответили. Обновите страницу.',
  DECISION_COMMENT_REQUIRED: 'Поясните, почему ремонт не заказываем.',
  TARGET_PTO_COMPLETED: 'Это ПТО уже завершено — выберите другое.',
  CODE_EXISTS: 'Такой код уже существует в справочнике.',
  DEFAULT_CANNOT_BE_INACTIVE: 'Значение по умолчанию нельзя деактивировать. Сначала назначьте другое.',

  // ── PTO crew — executor on writes (PTO crew, Phase 5a) ─────────────
  // POSITION_CHANGED / REPORT_CHANGED / MODE_SWITCH_AFFECTS_OTHERS have no entry by design:
  // every write that can raise them is handled in `features/applications/utils/staleWrite.ts` /
  // the mode-switch confirm dialogs, which show the server's own `message` (who changed what,
  // when) — a static fallback here would say less than the server already does. If one of these
  // three ever reaches this map, a handler is missing upstream.
  EXECUTOR_REQUIRED: 'Выберите исполнителя — без него запись не сохранится.',
  EXECUTOR_NOT_IN_CREW: 'Этот человек больше не в составе ПТО. Обновите страницу и выберите другого исполнителя.',
  // Owner D12 (replaces the D10 `ONLY_LEAD_CAN_COMPLETE`): with several mechanics only ШМ / НПТО /
  // the head of PTO / the web complete. `details.reason` refines it — see ERROR_REASON_MESSAGES.
  // Web users complete by their permission and never get it; the text is for any path that does.
  COMPLETION_NOT_ALLOWED: 'Когда механиков несколько, осмотр закрывает штатный механик, начальник ПТО или диспетчер.',
  PARTICIPANT_LIMIT_EXCEEDED: 'Лимит по этому типу исполнителей уже исчерпан.',
  PARTICIPANT_CANNOT_FILL: 'Этот тип исполнителя не заполняет отчёт.',
  TYPE_CANNOT_BE_LEAD: 'Этот тип исполнителя не может быть старшим.',
  INVALID_MECHANIC_TYPE: 'Недопустимый тип исполнителя для этого человека.',

  // ── Application versions (application-versions spec, 2026-10-01) ─────────────
  // The restore dialog reads these through `readVersionRestoreError.ts` and adds what the text
  // cannot carry: the people of OTHERS_RECENTLY_ACTIVE, the blockers of VERSION_RESTORE_BLOCKED.
  VERSION_NOT_FOUND: 'Версия не найдена или старше срока хранения.',
  NOTHING_TO_RESTORE: 'Эта версия не отличается от текущей — откатывать нечего.',
  APPLICATION_CHANGED: 'Пока вы смотрели, заявку изменили — проверьте отличия ещё раз.',
  APPLICATION_BUSY: 'Заявку прямо сейчас сохраняют — повторите через минуту.',
  OTHERS_RECENTLY_ACTIVE: 'С заявкой сейчас работают — отметьте, что вы в курсе, и подтвердите откат ещё раз.',
  VERSION_RESTORE_BLOCKED: 'Откат заблокирован — причины перечислены выше.',

  // ── Application merge (application-merge spec, 2026-10-02) ───────────────────
  // The merge window reads its refusals through `features/applications/merge/readMergeError.ts`,
  // which adds what a fixed text cannot carry: the number of the changed application, the
  // blockers found under the lock, the length of a joined text.
  MERGE_BLOCKED: 'Объединение заблокировано — причины перечислены в окне.',
  MERGE_CUSTOMER_MISMATCH: 'Объединять можно только заявки одного клиента.',
  MERGE_COMBINED_TOO_LONG: 'Склеенный текст не помещается в поле — выберите значение одной из заявок.',
  APPLICATION_MERGED: 'Заявка объединена с другой заявкой — работайте с итоговой.',
} as const;

/**
 * `details.reason` refinements of a backend code, keyed `CODE.REASON`. `getErrorMessage` checks
 * them before the plain code, so one code can say different things for different refusals.
 */
export const ERROR_REASON_MESSAGES: Record<string, string> = {
  'COMPLETION_NOT_ALLOWED.SEVERAL_MECHANICS':
    'Когда механиков несколько, осмотр закрывает штатный механик, начальник ПТО или диспетчер.',
  'COMPLETION_NOT_ALLOWED.NOT_A_MECHANIC': 'Осмотр закрывает механик ПТО, начальник ПТО или диспетчер.',
} as const;

// ============================================================================
// HTTP Status Codes
// ============================================================================

/**
 * HTTP status codes mapped to user-friendly Russian messages
 */
export const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'Некорректный запрос.',
  401: 'Требуется авторизация.',
  403: 'Доступ запрещён.',
  404: 'Страница или ресурс не найдены.',
  405: 'Метод не поддерживается.',
  408: 'Время ожидания истекло.',
  409: 'Конфликт данных: операцию нельзя выполнить в текущем состоянии. Обновите страницу; если ошибка повторяется — обратитесь к администратору системы.',
  410: 'Ресурс больше недоступен.',
  413: 'Размер запроса слишком большой.',
  415: 'Неподдерживаемый формат данных.',
  422: 'Ошибка обработки данных.',
  429: 'Слишком много запросов. Подождите немного.',
  500: 'Внутренняя ошибка сервера.',
  502: 'Сервер временно недоступен.',
  503: 'Сервис временно недоступен.',
  504: 'Время ожидания ответа сервера истекло.',
} as const;

// ============================================================================
// System/Network Errors
// ============================================================================

/**
 * System and network error messages
 */
export const SYSTEM_ERROR_MESSAGES = {
  /** Network connection error (no internet, server unreachable) */
  NETWORK_ERROR: 'Нет соединения с сервером. Проверьте интернет-соединение.',
  /** Request timeout */
  TIMEOUT_ERROR: 'Время ожидания истекло. Попробуйте позже.',
  /** Unknown/unexpected error */
  UNKNOWN_ERROR: 'Произошла неизвестная ошибка. Попробуйте позже.',
  /** Connection refused */
  CONNECTION_REFUSED: 'Не удалось подключиться к серверу. Попробуйте позже.',
  /** Request cancelled */
  REQUEST_CANCELLED: 'Запрос был отменён.',
} as const;

/**
 * Default error message for unknown errors
 */
export const DEFAULT_ERROR_MESSAGE = 'Произошла ошибка. Попробуйте позже.';

// ============================================================================
// Context-specific Error Messages
// ============================================================================

/**
 * Authentication-related error messages
 */
export const AUTH_ERROR_MESSAGES = {
  LOGIN_FAILED: 'Не удалось войти в систему.',
  LOGOUT_FAILED: 'Не удалось выйти из системы.',
  SESSION_EXPIRED: 'Сессия истекла. Пожалуйста, войдите заново.',
  REFRESH_FAILED: 'Не удалось обновить сессию. Войдите заново.',
} as const;

/**
 * CRUD operation error messages (for page-level fallbacks)
 */
export const CRUD_ERROR_MESSAGES = {
  LOAD_FAILED: 'Не удалось загрузить данные.',
  CREATE_FAILED: 'Не удалось создать запись.',
  UPDATE_FAILED: 'Не удалось обновить запись.',
  DELETE_FAILED: 'Не удалось удалить запись.',
  SAVE_FAILED: 'Не удалось сохранить данные.',
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get localized Russian error message for an error code
 *
 * @param code - Backend error code (e.g., 'UNAUTHORIZED', 'NOT_FOUND')
 * @returns Russian error message or default message if code not found
 *
 * @example
 * ```tsx
 * const message = getLocalizedErrorMessage('UNAUTHORIZED');
 * // Returns: 'Требуется авторизация. Пожалуйста, войдите в систему.'
 * ```
 */
export function getLocalizedErrorMessage(code: string): string {
  return ERROR_MESSAGES[code] ?? DEFAULT_ERROR_MESSAGE;
}

/**
 * Get localized Russian error message for an HTTP status code
 *
 * @param statusCode - HTTP status code (e.g., 401, 404, 500)
 * @returns Russian error message or default message if status code not found
 *
 * @example
 * ```tsx
 * const message = getHttpErrorMessage(404);
 * // Returns: 'Страница или ресурс не найдены.'
 * ```
 */
export function getHttpErrorMessage(statusCode: number): string {
  return HTTP_ERROR_MESSAGES[statusCode] ?? DEFAULT_ERROR_MESSAGE;
}

/**
 * Get localized Russian error message for a system error type
 *
 * @param errorType - System error type key
 * @returns Russian error message
 *
 * @example
 * ```tsx
 * const message = getSystemErrorMessage('NETWORK_ERROR');
 * // Returns: 'Нет соединения с сервером. Проверьте интернет-соединение.'
 * ```
 */
export function getSystemErrorMessage(
  errorType: keyof typeof SYSTEM_ERROR_MESSAGES
): string {
  return SYSTEM_ERROR_MESSAGES[errorType];
}

/**
 * Check if a message appears to be in Russian (contains Cyrillic characters)
 * Used to preserve API messages that are already in Russian
 *
 * @param message - Message to check
 * @returns true if message contains Cyrillic characters
 *
 * @example
 * ```tsx
 * isRussianMessage('Ошибка авторизации'); // true
 * isRussianMessage('Unauthorized'); // false
 * ```
 */
export function isRussianMessage(message: string): boolean {
  // Check for Cyrillic characters (Russian alphabet range)
  return /[\u0400-\u04FF]/.test(message);
}

// ============================================================================
// Type Exports
// ============================================================================

/**
 * System error type keys
 */
export type SystemErrorType = keyof typeof SYSTEM_ERROR_MESSAGES;

/**
 * Auth error type keys
 */
export type AuthErrorType = keyof typeof AUTH_ERROR_MESSAGES;

/**
 * CRUD error type keys
 */
export type CrudErrorType = keyof typeof CRUD_ERROR_MESSAGES;
